/**
 * What to fade, what to restore, and what to leave alone.
 *
 * Extracted from any viewer because it is the part with a wrong answer
 * available and no pixels involved: moving between two blocked faces must not
 * flash the body back to whole in between. Going from a sleeve's inner face to
 * the torso's left side keeps LeftArm ghosted the entire time — restoring it
 * and re-fading it is a visible blink that reads as a glitch.
 *
 * Shared so the editor and the homepage cannot drift apart on it. Both want
 * exactly this behaviour; only the alpha and the timing are theirs to choose.
 */

export interface GhostPlan {
  /** Parts to fade out — in `next` and not already faded. */
  ghost: string[];
  /** Parts to bring back — currently faded and no longer needed. */
  restore: string[];
  /** Already faded and still needed. Must not be touched. */
  keep: string[];
}

/**
 * Diff the currently-ghosted parts against what the new selection needs.
 *
 * Order in, order out: `occludersOf` does not return its list nearest-first —
 * `rightLimb.left` answers `["LeftArm", "Torso"]` though the torso is closer —
 * so nothing here may depend on ordering, and the output preserves input order
 * only to keep the result stable for tests and logs.
 */
export function ghostPlan(current: readonly string[], next: readonly string[]): GhostPlan {
  const now = new Set(current);
  const want = new Set(next);

  return {
    // Deduplicated: two regions can name the same blocker, and fading it twice
    // would start two tweens on one material.
    ghost: [...want].filter((p) => !now.has(p)),
    restore: [...now].filter((p) => !want.has(p)),
    keep: [...now].filter((p) => want.has(p)),
  };
}
